import React from 'react';
import { Lightbulb, Handshake, Sprout, Zap, MapPin, Users, Award, Calendar } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import './About.css';

const VALUES = [
  { icon: <Lightbulb size={22} />, title: 'Practical Innovation', desc: 'We focus on technologies that solve real business problems, not just what\'s trendy. Every solution must deliver measurable value.' },
  { icon: <Handshake size={22} />, title: 'Honest Delivery', desc: 'We set realistic expectations and deliver on our promises. No overpromising, no hidden costs, no surprises.' },
  { icon: <Sprout size={22} />, title: 'Long-Term Partnership', desc: 'We\'re not just vendors—we\'re your extended engineering team. Your success is our success.' },
  { icon: <Zap size={22} />, title: 'Engineering Excellence', desc: 'We maintain the highest standards in code quality, architecture, security, and performance.' },
];

const TEAM_ROLES = [
  { initials: 'AR', name: 'Amit Rajput', role: 'Founder & CEO', expertise: 'AI Strategy, Product Engineering' },
  { initials: 'SV', name: 'Senior AI Engineer', role: 'Head of AI', expertise: 'LLMs, Computer Vision, MLOps' },
  { initials: 'PK', name: 'Platform Architect', role: 'Head of Engineering', expertise: 'Cloud, Microservices, DevOps' },
  { initials: 'RN', name: 'Product Lead', role: 'Head of Product', expertise: 'UX, Growth, Client Strategy' },
];

const EXPERTISE = [
  'AI & Machine Learning', 'Full-Stack Development', 'Cloud Architecture',
  'Product Design', 'DevOps & Infrastructure', 'Mobile Development',
];

const STATS = [
  { icon: <Calendar size={18} />, label: 'Founded', value: '2023' },
  { icon: <MapPin size={18} />, label: 'Based in', value: 'India (Remote-first)' },
  { icon: <Users size={18} />, label: 'Team Size', value: '50+ Engineers' },
  { icon: <Award size={18} />, label: 'Projects', value: '50+ Delivered' },
];

function About() {
  usePageTitle('About Us');
  return (
    <div className="about">
      <section className="about-hero">
        <div className="about-container">
          <h1 className="about-hero-title">About Biltzentech</h1>
          <p className="about-hero-subtitle">Making AI practical for businesses worldwide</p>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="about-quick-stats">
        <div className="about-container">
          <div className="quick-stats-grid">
            {STATS.map((s, i) => (
              <div key={i} className="quick-stat-item">
                <span className="quick-stat-icon">{s.icon}</span>
                <div>
                  <div className="quick-stat-value">{s.value}</div>
                  <div className="quick-stat-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-story">
        <div className="about-container">
          <h2 className="section-title">Our Story</h2>
          <div className="story-content">
            <p>Biltzentech was founded by engineers who saw a gap in the market: companies were excited about AI but struggled to implement it in production. Too many agencies delivered demos and prototypes that never made it to real users.</p>
            <p>We started with a simple mission: build AI systems that actually work in production and deliver measurable business value. Not hype. Not demos. Real systems that handle real traffic and solve real problems.</p>
            <p>Today, we're a team of AI engineers, full-stack developers, and product strategists who partner with startups, SMBs, and enterprises to build intelligent software platforms.</p>
          </div>
        </div>
      </section>

      <section className="about-mission">
        <div className="about-container">
          <div className="mission-grid">
            <div className="mission-card">
              <h2>Mission</h2>
              <p>To make advanced AI technology accessible and practical for businesses of all sizes, delivering solutions that drive real ROI and competitive advantage.</p>
            </div>
            <div className="mission-card">
              <h2>Vision</h2>
              <p>To be the most trusted AI-first product engineering partner for companies building the future of intelligent software.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-values">
        <div className="about-container">
          <h2 className="section-title">Core Values</h2>
          <div className="values-grid">
            {VALUES.map((v, i) => (
              <div key={i} className="value-card">
                <div className="value-icon">{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-approach">
        <div className="about-container">
          <h2 className="section-title">Our Approach</h2>
          <div className="approach-content">
            <div className="approach-item">
              <h3>Business-First Thinking</h3>
              <p>We start with your business goals, not the technology. AI is a tool, not the solution itself.</p>
            </div>
            <div className="approach-item">
              <h3>Production-Grade Quality</h3>
              <p>Every system we build is designed for scale, security, and reliability from day one.</p>
            </div>
            <div className="approach-item">
              <h3>Transparent Communication</h3>
              <p>Regular updates, clear documentation, and open collaboration throughout the project.</p>
            </div>
            <div className="approach-item">
              <h3>Continuous Improvement</h3>
              <p>We don't just launch and leave. We monitor, optimize, and enhance your systems over time.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-team-cards">
        <div className="about-container">
          <h2 className="section-title">Leadership Team</h2>
          <div className="team-cards-grid">
            {TEAM_ROLES.map((member, i) => (
              <div key={i} className="team-card">
                <div className="team-avatar">{member.initials}</div>
                <h3 className="team-name">{member.name}</h3>
                <div className="team-role">{member.role}</div>
                <p className="team-expertise">{member.expertise}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-team">
        <div className="about-container">
          <h2 className="section-title">Engineering Expertise</h2>
          <div className="team-content">
            <p>We're a distributed team of 50+ engineers, designers, and strategists with expertise across:</p>
            <div className="expertise-grid">
              {EXPERTISE.map((item, i) => (
                <div key={i} className="expertise-item">{item}</div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
